import React, { useState, useEffect } from 'react';
import { adminApi, AdminUserDto, AdminUserDetailDto } from '../services/adminApi';
import { toast } from 'react-hot-toast';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  EllipsisVerticalIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';

const AdminBusinessUsersPage: React.FC = () => {
  const [users, setUsers] = useState<AdminUserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdminUserDetailDto | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userStats, setUserStats] = useState<any>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    fetchBusinessUsers();
    fetchUserStats();
  }, [currentPage, searchTerm, filterStatus]);

  const fetchBusinessUsers = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getUsers(currentPage, 20, searchTerm);
      
      // Filter only business users
      const businessUsers = response.data.users.filter((user: AdminUserDto) => user.userType === 'Business');
      
      setUsers(businessUsers);
      setPagination({
        ...response.data.pagination,
        totalUsers: businessUsers.length
      });
    } catch (error) {
      console.error('❌ Error fetching business users:', error);
              toast.error('خطا در بارگذاری کسب‌وکارها', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await adminApi.getUserStats();
      setUserStats(response.data);
    } catch (error) {
      // Stats loading failure is not critical
    }
  };

  const viewUserDetail = async (userId: string) => {
    try {
      const response = await adminApi.getUser(userId);
      setSelectedUser(response.data);
      setShowUserModal(true);
    } catch (error) {
      toast.error('خطا در بارگذاری جزئیات کسب‌وکار', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
    }
  };

  const handleVerifyUser = async (userId: string) => {
    setActionLoading('verify');
    try {
      await adminApi.verifyUser(userId);
      toast.success('کسب‌وکار با موفقیت تایید شد', {
        icon: '✅',
        style: {
          background: '#10B981',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
      fetchBusinessUsers();
    } catch (error) {
      toast.error('خطا در تایید کسب‌وکار', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspendUser = async (userId: string) => {
    setActionLoading('suspend');
    try {
              await adminApi.suspendUser(userId, 'Business policy violation');
              toast.success('کسب‌وکار با موفقیت تعلیق شد', {
        icon: '🚫',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
      fetchBusinessUsers();
    } catch (error) {
      toast.error('خطا در تعلیق کسب‌وکار', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (user: AdminUserDto) => {
    if (!user.isActive) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">تعلیق شده</span>;
    }
    if (user.isVerified) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">تایید شده</span>;
    }
          return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">در انتظار تایید</span>;
  };

  const filteredUsers = users.filter(user => {
    if (filterStatus === 'verified') return user.isVerified;
    if (filterStatus === 'pending') return !user.isVerified && user.isActive;
    if (filterStatus === 'suspended') return !user.isActive;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BuildingOfficeIcon className="w-8 h-8 text-cyan-600" />
          <div>
                          <h1 className="text-2xl font-semibold text-gray-900">مدیریت کسب‌وکارها</h1>
              <p className="text-sm text-gray-600">مدیریت و نظارت بر حساب‌های کسب‌وکار</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {userStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                <BuildingOfficeIcon className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-gray-900">{(userStats.businessUsers || 0).toLocaleString('fa-IR')}</p>
                <p className="text-sm text-gray-600">کل کسب‌وکارها</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircleIcon className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-gray-900">{filteredUsers.filter(u => u.isVerified).length}</p>
                <p className="text-sm text-gray-600">تایید شده</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-gray-900">{filteredUsers.filter(u => !u.isVerified && u.isActive).length}</p>
                <p className="text-sm text-gray-600">در انتظار تایید</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircleIcon className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-gray-900">{filteredUsers.filter(u => !u.isActive).length}</p>
                <p className="text-sm text-gray-600">تعلیق شده</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-4 w-full lg:w-auto">
            <div className="relative flex-1 lg:w-80">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="جستجوی کسب‌وکارها..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-5 h-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              >
                <option value="all">همه</option>
                <option value="verified">تایید شده</option>
                <option value="pending">در انتظار تایید</option>
                <option value="suspended">تعلیق شده</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Business Users Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">کسب‌وکار</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">مالک</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وضعیت</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">آگهی‌ها</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تاریخ عضویت</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">عملیات</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                        <BuildingOfficeIcon className="w-5 h-5 text-cyan-600" />
                      </div>
                      <div className="mr-4">
                        <div className="text-sm font-medium text-gray-900">{user.businessName || 'نام کسب‌وکار نامشخص'}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.firstName} {user.lastName}</div>
                    <div className="text-sm text-gray-500">{user.phoneNumber}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(user)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex flex-col">
                      <span className="font-medium">{user.totalAds} کل</span>
                      <span className="text-green-600">{user.activeAds} فعال</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.dateJoined).toLocaleDateString('fa-IR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2 gap-2-reverse">
                      <button
                        onClick={() => viewUserDetail(user.id)}
                        className="text-cyan-600 hover:text-cyan-900 p-1 rounded-md hover:bg-cyan-50"
                        title="مشاهده جزئیات"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      
                      {!user.isVerified && user.isActive && (
                        <button
                          onClick={() => handleVerifyUser(user.id)}
                          disabled={actionLoading === 'verify'}
                          className="text-green-600 hover:text-green-900 p-1 rounded-md hover:bg-green-50 disabled:opacity-50"
                          title="تایید کسب‌وکار"
                        >
                          <CheckCircleIcon className="w-4 h-4" />
                        </button>
                      )}
                      
                      {user.isActive && (
                        <button
                          onClick={() => handleSuspendUser(user.id)}
                          disabled={actionLoading === 'suspend'}
                          className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50 disabled:opacity-50"
                          title="تعلیق"
                        >
                          <XCircleIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">هیچ کسب‌وکاری یافت نشد</h3>
              <p className="mt-1 text-sm text-gray-500">هیچ کسب‌وکاری با این فیلترها یافت نشد.</p>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" dir="rtl">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">جزئیات کسب‌وکار</h3>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                                    <label className="block text-sm font-medium text-gray-700">نام کسب‌وکار</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser.businessName || 'نامشخص'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">مالک</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedUser.firstName} {selectedUser.lastName}</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">توضیحات کسب‌وکار</label>
                                      <p className="mt-1 text-sm text-gray-900">{selectedUser.businessDescription || 'توضیحی ارائه نشده است'}</p>
                </div>
                
                <div>
                                  <label className="block text-sm font-medium text-gray-700">وب‌سایت</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser.businessWebsite || 'وب‌سایتی ثبت نشده است'}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">ایمیل</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedUser.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">تلفن</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedUser.phoneNumber}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                                    <label className="block text-sm font-medium text-gray-700">شهر</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser.city || 'نامشخص'}</p>
                  </div>
                  <div>
                                    <label className="block text-sm font-medium text-gray-700">استان</label>
                <p className="mt-1 text-sm text-gray-900">{selectedUser.province || 'نامشخص'}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">کل آگهی‌ها</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedUser.totalAds}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">آگهی‌های فعال</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedUser.activeAds}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">وضعیت</label>
                    <div className="mt-1">{getStatusBadge(selectedUser as any)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBusinessUsersPage; 